import DynamicMultiSelectPicker from 'components/forms/form-components/select-picker/DynamicMultiSelectPicker';
import { PickerModalProps } from 'components/forms/form-components/select-picker/DynamicSelectPicker';
import { Props as DynamicMultiSelectInputProps } from 'components/forms/inputs/rhf-inputs/DynamicMultiSelectInput';
import React, { ReactNode } from 'react';
import { FieldValues } from 'react-hook-form';
import { MinDto } from 'types/other';
import { useLastNameFirst } from 'utils/nameOrder';
import { PartyNameParty, renderPartyNumberAndIcon } from 'utils/partyNameUtil';
import { buildContactEndAdornment } from './buildContactEndAdornment';
import { renderContactTags } from './renderContactTags';
import { resolveContactOptionLabel } from './resolveContactOptionLabel';

/**
 * Contact-aware wrapper around `DynamicMultiSelectPicker`. Same auto-adornment contract as the
 * other Contact* wrappers.
 */
type ContactDynamicMultiSelectPickerProps<
    TFormData extends FieldValues,
    TOption extends FieldValues & PartyNameParty,
    TPickedOption extends MinDto,
    TCreatedOption extends MinDto,
    TModalProps extends PickerModalProps<TPickedOption, TCreatedOption> = PickerModalProps<
        TPickedOption,
        TCreatedOption
    >,
> = Omit<
    DynamicMultiSelectInputProps<TFormData, TOption>,
    'getOptionStartAdornment' | 'getOptionEndAdornment' | 'getOptionLabel' | 'getOptionIdentifier'
> & {
    PickerModalComponent: React.ComponentType<TModalProps>;
    isPickerModalOptionEnabled?: (option: TPickedOption | TCreatedOption) => boolean;
    /** Defaults to `"name"`. */
    getOptionLabel?: DynamicMultiSelectInputProps<TFormData, TOption>['getOptionLabel'];
    /** Defaults to `"id"`. */
    getOptionIdentifier?: DynamicMultiSelectInputProps<TFormData, TOption>['getOptionIdentifier'];
    /** Optional extra end adornment (e.g. `renderPartyEmail`). Rendered BEFORE the PNG chip. */
    getOptionEndAdornment?: (option: TOption) => ReactNode;
};

const ContactDynamicMultiSelectPicker = <
    TFormData extends FieldValues,
    TOption extends FieldValues & PartyNameParty,
    TPickedOption extends MinDto,
    TCreatedOption extends MinDto,
>(
    props: ContactDynamicMultiSelectPickerProps<TFormData, TOption, TPickedOption, TCreatedOption>
) => {
    const {
        getOptionEndAdornment: callerEndAdornment,
        getOptionLabel = 'name' as DynamicMultiSelectInputProps<TFormData, TOption>['getOptionLabel'],
        getOptionIdentifier = 'id' as DynamicMultiSelectInputProps<TFormData, TOption>['getOptionIdentifier'],
        renderTags: callerRenderTags,
        ...rest
    } = props;

    const getOptionEndAdornment = (option: TOption): ReactNode => {
        const extra = callerEndAdornment ? callerEndAdornment(option) : null;
        return buildContactEndAdornment(option, extra);
    };

    const lastNameFirst = useLastNameFirst();
    const resolveLabel = (option: TOption): string | null =>
        resolveContactOptionLabel(option, getOptionLabel as string | ((o: TOption) => string), lastNameFirst);

    return (
        <DynamicMultiSelectPicker
            {...rest}
            getOptionLabel={getOptionLabel}
            getOptionIdentifier={getOptionIdentifier}
            getOptionStartAdornment={renderPartyNumberAndIcon}
            getOptionEndAdornment={getOptionEndAdornment}
            renderTags={
                callerRenderTags ?? ((value, getTagProps) => renderContactTags(resolveLabel, value, getTagProps))
            }
        />
    );
};

export default ContactDynamicMultiSelectPicker;
